import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import "./styles.css";

import {
  createAppI18n,
  directionFor,
  loadModulesI18n,
  useLanguageStore,
} from "@nasam/core";
import { AppProviders } from "./app/providers/AppProviders";
import { createAppRouter } from "./app/router";
import { authController } from "./app/auth";
import { verticalFeatures } from "./app/registry";

// Defaults to DEV; VITE_USE_MOCK_API overrides either way, so dev can point at
// a live backend and a prod build can still demo against MSW.
function shouldUseMockApi(): boolean {
  const override = import.meta.env.VITE_USE_MOCK_API;
  if (override === "true") return true;
  if (override === "false") return false;
  return import.meta.env.DEV;
}

if (shouldUseMockApi()) {
  const { startMockWorker } = await import("./mocks/browser");
  try {
    await startMockWorker();
  } catch (error) {
    // A worker failure (e.g. an untrusted dev cert blocking Service Worker
    // registration) must not blank the whole app before it can even render.
    console.error("[MSW] Failed to start the mock worker:", error);
  }
}

const rootElement = document.getElementById("root");

if (rootElement === null) {
  throw new Error('Host shell requires an element with id "root".');
}

const initialLanguage = useLanguageStore.getState().language;

// index.html ships the default language; a persisted choice only reaches the
// document here, and every logical utility resolves against <html dir>.
document.documentElement.lang = initialLanguage;
document.documentElement.dir = directionFor(initialLanguage);

// The non-CORE business modules (fleet, operations, ...) ship their own i18n
// bundle rather than a static one in @nasam/core/i18n, so their namespace only
// loads if the modules list is passed here — see loadModuleI18n. The CORE
// features are excluded: their declared i18n.namespace collides with a
// static bundle already in i18nResources, and loadModuleI18n warns and skips
// in that case.
const i18n = await createAppI18n(initialLanguage, verticalFeatures);

void authController.initialize();

const router = createAppRouter({
  // Runtime-registered remotes bring their own translations; without this a
  // federated module would render raw i18n keys until the host rebuilt.
  onRuntimeModulesLoaded: (modules) =>
    loadModulesI18n(i18n, modules, useLanguageStore.getState().language),
});

createRoot(rootElement).render(
  <StrictMode>
    <AppProviders i18n={i18n}>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);
